import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
    Heading2,
    TextDefault,
    ThemedButton,
    Block,
    TextLead,
    CheckboxInput,
    palette,
} from '@awarego-ui/components';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import services from '../../services';
import { useStores } from '../../hooks/use-stores';
import Sidebar from '../../components/manager/assessment/sidebar';
import HeaderSection from '../../components/header/HeaderSection';
import SubHeaderComponentsList from '../../components/header/SubHeaderComponentsList';
import TableServer from '../../components/table/TableServer';
import StyledLink from '../../components/StyledLink';
import SubjectSelect from '../../components/manager/automation/subject-select-ta-and-kb';
import SubjectsOrder from '../../components/manager/automation/subjects-order';
import AutomationReminders from '../../components/manager/automation/reminders';
import SubjectPreview from '../subjectPreview';
import Automation from '../../stores/models/automation';
import {
    TextField,
    Box,
    Paper,
    Alert,
    Typography,
} from '@mui/material';
import {
    AUTOMATION_SCHEDULE_TYPE,
    STATUS,
    TRANSPORTS,
} from '../../utils/constants';
import '../../components/manager/assessment/sidebar.scss';

const WIZARD_STEP_LABELS = [
    'Campaign',
    'Targets',
    'Training — Content',
    'Training — Reminders',
    'Training — Summary',
];

const STEPS_FOR_SIDEBAR = WIZARD_STEP_LABELS.map((title, i) => ({
    number: i + 1,
    title,
}));

function toDateTimeLocalValue(date = new Date()) {
    const t = new Date(date);
    t.setSeconds(0, 0);
    t.setMilliseconds(0);
    const pad = (n) => String(n).padStart(2, '0');
    return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}T${pad(t.getHours())}:${pad(t.getMinutes())}`;
}

function formatWhen(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return String(iso);
    }
}

function wizardIntegrationHintLines(diag) {
    if (!diag || typeof diag !== 'object') return [];
    const lines = [];
    if (diag.connection && diag.connection.ok === false) {
        lines.push(
            `Microsoft Graph: not ready — ${String(diag.connection.error || 'unknown error')}`
        );
    }
    const tr = diag.training;
    if (tr && tr.issue === 'no_lms_domain_or_LMS_SERVER_URL') {
        lines.push(
            'Training URL: missing — set company/partner lms_domain or set LMS_SERVER_URL on awarego-api.'
        );
    } else if (tr && tr.issue === 'training_url_is_http_graph_expects_https') {
        lines.push(
            `Training URL: uses http — Graph requires https (resolved: ${tr.url || '—'}).`
        );
    } else if (tr && tr.url && !tr.issue) {
        lines.push(`Training URL: OK (${tr.source}) — ${tr.url}`);
    }
    if (diag.pages && diag.pages.ok === false) {
        lines.push(
            `Landing/login pages: Graph error — ${String(diag.pages.error || 'unknown')}`
        );
    } else if (diag.pages && diag.pages.ok) {
        if (diag.pages.resolvedVia === 'microsoft_attack_simulation_catalog') {
            lines.push(
                'Landing/login pages: selected from the Microsoft Attack Simulation catalog (Graph documents List/Get only for these resources).'
            );
        } else {
            lines.push('Landing/login pages: ready.');
        }
    }
    const n = diag.notifications;
    if (n && n.listError) {
        lines.push(`End-user notifications: list failed — ${String(n.listError)}`);
    } else if (n && n.count === 0) {
        lines.push(
            'End-user notifications: Microsoft returned none for this tenant (Defender AST / licensing).'
        );
    } else if (n && n.count > 0 && !n.selectedId) {
        lines.push('End-user notifications: list returned rows but none could be selected.');
    } else if (n && n.selectedId) {
        lines.push(`End-user notifications: OK (${n.count} template(s), one selected).`);
    }
    return lines;
}

function AstPhishingSimulationWizard({
    open,
    onClose,
    companyId,
    svc,
    initialPayload,
    wizardDefaults,
    onCreated,
}) {
    const {
        commonStore,
        catalogStore,
        store,
        listStore,
        automationStore,
        companyStore,
    } = useStores();
    const { slackAvailable, teamsAvailable } = store;

    const [wizardStep, setWizardStep] = useState(0);
    const [campaignName, setCampaignName] = useState('');
    const [campaignLaunch, setCampaignLaunch] = useState('');
    const [companyLists, setCompanyLists] = useState([]);
    const [listsLoading, setListsLoading] = useState(false);
    const [selectedListIds, setSelectedListIds] = useState([]);
    const [listSearch, setListSearch] = useState('');
    const [listsTableQuery, setListsTableQuery] = useState({
        p: 0,
        n: 10,
        order: 'listName asc',
    });
    const [resolvingTargets, setResolvingTargets] = useState(false);
    const [trainingAutomation, setTrainingAutomation] = useState(null);
    const [previewSubjectId, setPreviewSubjectId] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    const needsLoginPage =
        (initialPayload &&
            (initialPayload.attackTechnique || 'credentialHarvesting') ===
                'credentialHarvesting') ||
        false;

    const integrationReady =
        wizardDefaults &&
        wizardDefaults.awarego &&
        wizardDefaults.awarego.landingPageId &&
        wizardDefaults.endUserNotification &&
        wizardDefaults.endUserNotification.id &&
        (!needsLoginPage || wizardDefaults.awarego.loginPageId) &&
        /^https:\/\//i.test(String(wizardDefaults.awarego.trainingUrl || ''));

    const integrationHintLines = useMemo(
        () =>
            wizardIntegrationHintLines(
                wizardDefaults && wizardDefaults.integrationDiagnostics
            ),
        [wizardDefaults]
    );

    const resetAll = useCallback(() => {
        setWizardStep(0);
        setCampaignName('');
        setCampaignLaunch('');
        setCompanyLists([]);
        setSelectedListIds([]);
        setListSearch('');
        setListsTableQuery({ p: 0, n: 10, order: 'listName asc' });
        setTrainingAutomation(null);
        setPreviewSubjectId(null);
        setPreviewOpen(false);
    }, []);

    useEffect(() => {
        if (!open) {
            resetAll();
            return;
        }
        const p = initialPayload;
        if (!p) return;
        const defaultName =
            p.name || p.displayName || 'Phishing awareness simulation';
        setCampaignName(defaultName);
        const t = new Date(Date.now() + 86400000);
        t.setMinutes(0, 0, 0);
        setCampaignLaunch(toDateTimeLocalValue(t));
    }, [open, initialPayload, resetAll]);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setListsLoading(true);
        services.Companies.listsService(companyId)
            .list()
            .then((rows) => {
                if (!cancelled) setCompanyLists(Array.isArray(rows) ? rows : []);
            })
            .catch(() => {
                if (!cancelled) setCompanyLists([]);
            })
            .finally(() => {
                if (!cancelled) setListsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, companyId]);

    useEffect(() => {
        if (!open) return;
        catalogStore.loadCatalog(companyId);
    }, [open, companyId, catalogStore]);

    useEffect(() => {
        if (!open) return;
        companyStore.loadIntegrationData(companyId, 'slack');
        companyStore.loadIntegrationData(companyId, 'teams');
    }, [open, companyId, companyStore]);

    useEffect(() => {
        if (!open) {
            setTrainingAutomation(null);
            return;
        }
        const a = new Automation(
            listStore,
            catalogStore,
            store,
            companyId,
            automationStore.questionsIndex,
            null,
            store.isTrial,
            store.havePartnerSubscription,
            null,
            store.enabledLanguages || []
        );
        a.setScheduleType(AUTOMATION_SCHEDULE_TYPE.DOWNLOAD_LINKS);
        a.setSigninLinkValidity('30d');
        if (a.subjectsPerInterval == null) {
            a.subjectsPerInterval = 3;
        }
        a.status = STATUS.SCHEDULED;
        setTrainingAutomation(a);
        return () => setTrainingAutomation(null);
    }, [
        open,
        companyId,
        listStore,
        catalogStore,
        store,
        automationStore.questionsIndex,
        store.isTrial,
        store.havePartnerSubscription,
        store.enabledLanguages,
    ]);

    useEffect(() => {
        if (!trainingAutomation) return;
        const base = campaignName.trim() || 'Phishing follow-up training';
        trainingAutomation.setName(`${base} — follow-up`);
    }, [campaignName, trainingAutomation]);

    const filteredLists = useMemo(() => {
        const q = listSearch.trim().toLowerCase();
        if (!q) return companyLists;
        return companyLists.filter((l) =>
            String(l.name || '')
                .toLowerCase()
                .includes(q)
        );
    }, [companyLists, listSearch]);

    useEffect(() => {
        setListsTableQuery((q) => ({ ...q, p: 0 }));
    }, [listSearch]);

    const listsTableRows = useMemo(
        () =>
            filteredLists.map((l) => ({
                listId: l.id,
                listName: l.name,
                employeesCount: l.usersCount ?? '—',
                completionRate: 0,
                resilienceScore: 0,
            })),
        [filteredLists]
    );

    const listsPageData = useMemo(() => {
        const { p, n } = listsTableQuery;
        const start = p * n;
        return listsTableRows.slice(start, start + n);
    }, [listsTableRows, listsTableQuery]);

    const listColumns = useMemo(
        () => [
            {
                Header: 'List',
                accessor: 'listName',
                Cell: (x) => (
                    <CheckboxInput
                        label={
                            <StyledLink
                                to={`/users/list/${x.row.original.listId}`}
                                $hoverColor={palette.graphite.onyx}
                                $underlined={true}
                            >
                                {x.value}
                            </StyledLink>
                        }
                        checked={selectedListIds.includes(x.row.original.listId)}
                        noYPadding
                        onChange={(e) => {
                            const id = x.row.original.listId;
                            setSelectedListIds((prev) =>
                                e.target.checked
                                    ? prev.includes(id)
                                        ? prev
                                        : [...prev, id]
                                    : prev.filter((y) => y !== id)
                            );
                        }}
                    />
                ),
            },
            {
                Header: '# Employees',
                accessor: 'employeesCount',
                Cell: (x) => x.value,
            },
            {
                Header: 'Completion rate',
                accessor: 'completionRate',
                Cell: (x) => `${x.value}%`,
            },
            {
                Header: 'Average score',
                accessor: 'resilienceScore',
                Cell: (x) => `${x.value}%`,
            },
        ],
        [selectedListIds]
    );

    const pageListIds = useMemo(
        () => listsPageData.map((r) => r.listId),
        [listsPageData]
    );

    const selectedOnPageCount = useMemo(
        () => pageListIds.filter((id) => selectedListIds.includes(id)).length,
        [pageListIds, selectedListIds]
    );

    const currentPageAllSelected =
        pageListIds.length > 0 && selectedOnPageCount === pageListIds.length;
    const currentPageIndeterminate =
        selectedOnPageCount > 0 && selectedOnPageCount < pageListIds.length;

    const handleSelectAllOnPage = (checked) => {
        setSelectedListIds((prev) => {
            const set = new Set(prev);
            if (checked) {
                pageListIds.forEach((id) => set.add(id));
            } else {
                pageListIds.forEach((id) => set.delete(id));
            }
            return [...set];
        });
    };

    const onChangeListsPaging = useCallback((p, n) => {
        setListsTableQuery((q) => ({ ...q, p, n }));
    }, []);

    const onChangeListsSort = useCallback((sortBy) => {
        if (!sortBy || sortBy.length === 0) return;
        const order = `${sortBy[0].id} ${sortBy[0].desc ? 'desc' : 'asc'}`;
        setListsTableQuery((q) => ({ ...q, order, p: 0 }));
    }, []);

    const allowedSidebarSteps = useMemo(
        () =>
            STEPS_FOR_SIDEBAR.map((s) => s.number).filter((n) => n <= wizardStep + 1),
        [wizardStep]
    );

    const canWizardNext = () => {
        if (!integrationReady) return false;
        if (wizardStep === 0) {
            return !!(campaignName.trim() && campaignLaunch);
        }
        if (wizardStep === 1) {
            return selectedListIds.length > 0;
        }
        if (wizardStep === 2) {
            return (
                !!trainingAutomation &&
                trainingAutomation.selectedVideosIds.length > 0
            );
        }
        if (wizardStep === 3) return true;
        if (wizardStep === 4) return true;
        return true;
    };

    const wizardNext = () => {
        if (wizardStep < WIZARD_STEP_LABELS.length - 1) {
            setWizardStep((s) => s + 1);
        }
    };

    const wizardBack = () => {
        if (wizardStep > 0) setWizardStep((s) => s - 1);
    };

    const onChangeRemindersChannel = (event) => {
        if (!trainingAutomation) return;
        if (
            !trainingAutomation.setRemindersChannel(
                Number(event.target.value),
                event.target.checked
            )
        ) {
            commonStore.warn('At least one channel needs to be selected');
        }
    };

    const onDeleteReminderStep = (item) => {
        if (!trainingAutomation) return;
        trainingAutomation.deleteReminderStep(item);
    };

    const onAddReminderStep = () => {
        if (!trainingAutomation) return;
        trainingAutomation.addReminderStep();
    };

    const submitWizard = async () => {
        if (!initialPayload || !integrationReady || !trainingAutomation) return;
        setResolvingTargets(true);
        try {
            const training = {
                ...trainingAutomation.toDTO({
                    templateId: [-1],
                    type: 'training',
                }),
                introMessageEnabled: false,
                introductoryData: {
                    subject: campaignName.trim() || 'Phishing simulation',
                    message: '',
                    mrkdwn: '',
                },
                assessmentEnabled: false,
                showRightAnswers: false,
                questions: [],
                templateId: [-1],
                timeOfDay: null,
                weekday: null,
            };
            delete training.userLists;
            delete training.id;
            if (!training.reminders_enabled) {
                training.reminders_settings = {
                    channel: TRANSPORTS.EMAIL,
                    steps: [],
                };
            }

            await svc.createWizardSimulation({
                payload: initialPayload,
                campaignName: campaignName.trim(),
                campaignLaunch: new Date(campaignLaunch).toISOString(),
                sourceListIds: selectedListIds,
                training,
            });
            commonStore.showMessage(
                'Follow-up training and Microsoft 365 simulation were created.',
                'success'
            );
            if (onCreated) {
                onCreated();
            }
            resetAll();
            onClose();
        } catch (e) {
            commonStore.showMessage(e.message || String(e), 'error');
        } finally {
            setResolvingTargets(false);
        }
    };

    if (!open || !initialPayload) {
        return null;
    }

    return (
        <div className="training-wizard wizard-fullscreen">
            <div className="training-wizard-header">
                <img
                    src="/static/img/close.svg"
                    alt="Close"
                    className="training-wizard-close"
                    onClick={onClose}
                    role="presentation"
                />
                <Heading2 displayBlock style={{ margin: 0 }}>
                    Create phishing simulation
                </Heading2>
            </div>
            <div className="training-wizard-body">
                <Sidebar
                    steps={STEPS_FOR_SIDEBAR}
                    activeStep={wizardStep + 1}
                    allowedSteps={allowedSidebarSteps}
                    onStepChange={(n) => setWizardStep(n - 1)}
                    warningSteps={[]}
                />
                <Box
                    component="div"
                    className="training-wizard-content expand-content"
                    sx={{
                        maxWidth: 'none !important',
                        minWidth: 0,
                        flex: '1 1 auto',
                        pt: { xs: 3, sm: 4, md: 5 },
                        pr: { xs: 3, sm: 5, md: 8 },
                        pb: { xs: 4, md: 6 },
                        pl: { xs: 3, sm: 5, md: 8 },
                    }}
                >
                    {!integrationReady && (
                        <Alert severity="warning" sx={{ mb: 3, maxWidth: 760 }}>
                            <Typography component="div" variant="body2" sx={{ mb: 1 }}>
                                This wizard cannot run until integration and training
                                settings are complete. Update your company setup and reload
                                the page.
                            </Typography>
                            {integrationHintLines.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                        Details
                                    </Typography>
                                    <Box
                                        component="ul"
                                        sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}
                                    >
                                        {integrationHintLines.map((t, i) => (
                                            <li key={i}>
                                                <Typography variant="body2">{t}</Typography>
                                            </li>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Alert>
                    )}

                    {wizardStep === 0 && (
                        <Box sx={{ maxWidth: 720 }}>
                            <TextField
                                label="Campaign name"
                                fullWidth
                                margin="normal"
                                value={campaignName}
                                onChange={(e) => setCampaignName(e.target.value)}
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    alignItems: 'flex-start',
                                    flexWrap: 'wrap',
                                    mt: 1,
                                }}
                            >
                                <TextField
                                    label="Launch (local time)"
                                    type="datetime-local"
                                    margin="dense"
                                    sx={{ flex: '1 1 220px', minWidth: 220 }}
                                    InputLabelProps={{ shrink: true }}
                                    value={campaignLaunch}
                                    onChange={(e) =>
                                        setCampaignLaunch(e.target.value)
                                    }
                                />
                                <ThemedButton
                                    size="small"
                                    mode="secondary"
                                    text="Now"
                                    style={{ marginTop: 8 }}
                                    onClick={() =>
                                        setCampaignLaunch(toDateTimeLocalValue())
                                    }
                                />
                            </Box>
                            <TextDefault lighter style={{ marginTop: 12 }}>
                                Template:{' '}
                                <strong>
                                    {initialPayload.name ||
                                        initialPayload.displayName}
                                </strong>{' '}
                                ({initialPayload.attackTechnique || '—'})
                            </TextDefault>
                        </Box>
                    )}

                    {wizardStep === 1 && (
                        <Box sx={{ maxWidth: 960 }}>
                            {listsLoading ? (
                                <ThemedSpinner />
                            ) : (
                                <Block gap={24} tablePadding noBorder>
                                    <HeaderSection
                                        heading={
                                            <TextLead bold>{`Lists (${listsTableRows.length})`}</TextLead>
                                        }
                                        searchPlaceholder="Search lists"
                                        searchValue={listSearch}
                                        onSearch={(e) =>
                                            setListSearch(e.target.value)
                                        }
                                        onClearSearch={() => setListSearch('')}
                                        subHeadings={
                                            <SubHeaderComponentsList>
                                                <CheckboxInput
                                                    label={
                                                        selectedOnPageCount === 0 ||
                                                        selectedOnPageCount ===
                                                            pageListIds.length
                                                            ? `Select all (${pageListIds.length})`
                                                            : `Selected (${selectedOnPageCount}/${pageListIds.length})`
                                                    }
                                                    checked={currentPageAllSelected}
                                                    indeterminate={currentPageIndeterminate}
                                                    onChange={(e) =>
                                                        handleSelectAllOnPage(
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                            </SubHeaderComponentsList>
                                        }
                                        excludeBlock
                                        marginLeft="16"
                                    />
                                    <TableServer
                                        columns={listColumns}
                                        data={listsPageData}
                                        rowsTotal={listsTableRows.length}
                                        pagesTotal={Math.ceil(
                                            listsTableRows.length /
                                                listsTableQuery.n
                                        )}
                                        initialPageSize={listsTableQuery.n}
                                        defaultSortBy="listName"
                                        defaultSortDescent={false}
                                        hidePagination={
                                            listsTableRows.length <= listsTableQuery.n
                                        }
                                        onChangePagingParams={onChangeListsPaging}
                                        onChangeSort={onChangeListsSort}
                                        loadingData={false}
                                    />
                                </Block>
                            )}
                        </Box>
                    )}

                    {wizardStep === 2 && trainingAutomation && (
                        <Box sx={{ maxWidth: 900 }}>
                            <SubjectSelect
                                catalog={catalogStore.catalog || []}
                                onVideoSelected={(id) =>
                                    trainingAutomation.toggleVideoSelected(id)
                                }
                                selection={trainingAutomation.selectedVideosIds.slice()}
                                showPreviewSubjectModal={(id) => {
                                    setPreviewSubjectId(id);
                                    setPreviewOpen(true);
                                }}
                                category="all"
                                isTrial={store.isTrial}
                            />
                            <TextDefault style={{ marginTop: 24, marginBottom: 8 }}>
                                <strong>Order</strong>
                            </TextDefault>
                            <SubjectsOrder
                                videos={trainingAutomation.videosSelected}
                                onReorderList={(a, b) =>
                                    trainingAutomation.reorderSubjectList(a, b)
                                }
                                onRemoveSubject={(id) =>
                                    trainingAutomation.toggleVideoSelected(id)
                                }
                                isTrial={store.isTrial}
                                templates={automationStore.rawTemplates}
                            />
                        </Box>
                    )}

                    {wizardStep === 3 && trainingAutomation && (
                        <Box sx={{ maxWidth: 800 }}>
                            <AutomationReminders
                                reminders_enabled={trainingAutomation.reminders_enabled}
                                reminders_channel={trainingAutomation.reminders_channel}
                                reminders_steps={trainingAutomation.reminders_steps}
                                slackAvailable={slackAvailable}
                                slackConnected={automationStore.slackConnected}
                                teamsAvailable={teamsAvailable}
                                teamsConnected={automationStore.teamsConnected}
                                onChangeRemindersEnabled={() =>
                                    trainingAutomation.setRemindersEnabled()
                                }
                                onChangeRemindersChannel={onChangeRemindersChannel}
                                onAddReminderStep={onAddReminderStep}
                                onDeleteReminderStep={onDeleteReminderStep}
                                scheduleType={trainingAutomation.scheduleType}
                                mt={16}
                            />
                        </Box>
                    )}

                    {wizardStep === 4 && trainingAutomation && (
                        <Box sx={{ maxWidth: 760 }}>
                            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                <ul style={{ paddingLeft: 20, margin: 0 }}>
                                    <li>
                                        <strong>Campaign:</strong> {campaignName}
                                    </li>
                                    <li>
                                        <strong>Launch:</strong>{' '}
                                        {formatWhen(
                                            new Date(campaignLaunch).toISOString()
                                        )}
                                    </li>
                                    <li>
                                        <strong>Payload:</strong>{' '}
                                        {initialPayload.name ||
                                            initialPayload.displayName}
                                    </li>
                                    <li>
                                        <strong>Simulation target lists:</strong>{' '}
                                        {selectedListIds.length} selected
                                    </li>
                                    <li>
                                        <strong>Training subjects:</strong>{' '}
                                        {trainingAutomation.selectedVideosIds.length}
                                    </li>
                                    <li>
                                        <strong>Reminders:</strong>{' '}
                                        {trainingAutomation.reminders_enabled
                                            ? 'On'
                                            : 'Off'}
                                    </li>
                                </ul>
                            </Paper>
                        </Box>
                    )}
                </Box>
            </div>
            <div className="training-wizard-footer">
                <div className="actions" style={{ padding: '0 24px' }}>
                    <ThemedButton mode="secondary" text="Cancel" onClick={onClose} />
                    <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                        {wizardStep > 0 && (
                            <ThemedButton
                                mode="secondary"
                                text="Back"
                                onClick={wizardBack}
                            />
                        )}
                        {wizardStep < WIZARD_STEP_LABELS.length - 1 && (
                            <ThemedButton
                                mode="primary"
                                text="Next"
                                onClick={wizardNext}
                                disabled={!canWizardNext()}
                            />
                        )}
                        {wizardStep === WIZARD_STEP_LABELS.length - 1 && (
                            <ThemedButton
                                mode="primary"
                                text={
                                    resolvingTargets
                                        ? 'Creating…'
                                        : 'Create training & simulation'
                                }
                                onClick={submitWizard}
                                disabled={!canWizardNext() || resolvingTargets}
                            />
                        )}
                    </div>
                </div>
            </div>
            {previewOpen && previewSubjectId != null && (
                <SubjectPreview
                    subjectId={previewSubjectId}
                    isOpen={previewOpen}
                    onClose={() => {
                        setPreviewOpen(false);
                        setPreviewSubjectId(null);
                    }}
                />
            )}
        </div>
    );
}

export default observer(AstPhishingSimulationWizard);
